import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from "utils/axios";
import Skeleton from "@mui/material/Skeleton"
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getVendorQuotation, submitLabourQuotation } from "actions/vendorQuotationAction";
import { useSnackbar } from 'notistack';
import CustomGstDropdown from "./CustomGstDropdown";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import StaticPopup from "components/StaticPopup";
import FileUpload from "./FileUpload";
import { vendorQuotationService } from "services/vendorQuotationService";
import ReactHtmlParser from "react-html-parser";
import ReactQuill from "react-quill";
import RfqInformation from "./RfqInformation";
import { handleDownloadLabourExcel, handleUploadLabourExcel } from "./labourExcelHelper";
import LoadingModal from "views/Rfqs/LoadingModal";
import QuotationDesktopStyles from "./QuotationDesktopStyles";
import QuotationTopNav from "./QuotationTopNav";
import { getFormattedUnit } from "config";
import { shouldShowVendorRank } from "./utils/rankVisibility";
import QuotationPdfPreviewButton from "./QuotationPdfPreviewButton";
import QuotationUpdateReasonModal from "./QuotationUpdateReasonModal";
import RfqUnavailableNotice from "./RfqUnavailableNotice";

const MobileSectionCard = ({ number, title, description, children }) => (
    <section className="rounded-2xl border border-[#E6E6DD] bg-white shadow-sm overflow-hidden">
        <div className="flex gap-3 border-b border-[#EFEFE8] bg-[#FBFBF7] px-4 py-4">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#ff6700] text-xs font-semibold text-white">
                {number}
            </div>
            <div>
                <h3 className="text-base font-semibold text-[#2F2F22]">{title}</h3>
                {description && <p className="mt-1 text-xs leading-5 text-[#7A7A68]">{description}</p>}
            </div>
        </div>
        <div className="p-4">
            {children}
        </div>
    </section>
);

const formatAmount = (value) =>
    new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value) || 0);

const getQuotationUnavailableMessage = (error) => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message || "";
    const normalizedMessage = String(message).toLowerCase();

    if (status === 403) return message || "You no longer have access to this RFQ.";
    if (status === 404 && (normalizedMessage.includes("rfq not found") || normalizedMessage.includes("no longer available"))) {
        return message || "This RFQ is no longer available.";
    }

    return null;
};
const isCounterOfferSelected = (value) => value === false || value === "false";

const isMobileQuotationView = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;

const LabourQuotationForm = () => {

    const quotationPdfRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    // RFQ data from location
    // const rfqData = location.state?.rfqData;
    const { loading, error, errorMessage } = useSelector((state) => state.vendorQuotation);
    const { enqueueSnackbar } = useSnackbar();

    const auth = useSelector((state) => state.auth);
    const userProfile = auth.user;
    const searchParams = new URLSearchParams(location.search);
    const queryRfqId = searchParams.get("rfqId");
    const getStoredRfqData = () => {
        if (!queryRfqId) return {};

        try {
            return JSON.parse(sessionStorage.getItem(`rfq-quotation-view-${queryRfqId}`) || "{}")?.rfqData || {};
        } catch (error) {
            console.error("Failed to read stored RFQ data:", error);
            return {};
        }
    };
    const [rfqData, setRfqData] = useState(location.state?.rfqData || getStoredRfqData() || {});
    const [excelLoading, setExcelLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [excelError, setExcelError] = useState(null);
    const isEdit = location.state?.isEdit;
    const supplierId = location.state?.supplierId || searchParams.get("supplierId") || "";
    const queryQuotationIndex = searchParams.get("quotationIndex");
    const quotationIndex = location.state?.quotationIndex ?? (queryQuotationIndex !== null ? Number(queryQuotationIndex) : undefined);
    const currentSupplier = rfqData?.suppliers?.find(item => String(item?.supplierId) === String(supplierId)) || rfqData?.suppliers?.[0];
    const canEditRequestedQuotation = currentSupplier?.quotationUpdateRequest?.isRequested === true;
    const hasSubmittedQuotation = Boolean(currentSupplier?.quotationReceived || currentSupplier?.vendorQuotations?.quotationSets?.length);
    const isRfqCreatorUpdatingSubmittedQuotation = String(rfqData?.requesterId || "") === String(userProfile?.userId || "") && hasSubmittedQuotation;
    const [showQuotationUpdateReasonModal, setShowQuotationUpdateReasonModal] = useState(false);
    const [pendingQuotationPayload, setPendingQuotationPayload] = useState(null);
    const [quotationUnavailableMessage, setQuotationUnavailableMessage] = useState(null);
    const isReadOnly = quotationIndex !== undefined || searchParams.get("readOnly") === "true" || (rfqData?.status === "PENDING APPROVAL" && !canEditRequestedQuotation) || rfqData?.status === "CLOSED"

    useEffect(() => {
        const fetchVendorQuotation = async () => {
            if (supplierId) {
                try {
                    const payload = { rfqId: rfqData?.rfqId, supplierId };
                    const response = await vendorQuotationService.getVendorQuotation(payload);

                    // Update rfqData.suppliers[0] with fetched vendorQuotations
                    if (response?.vendorQuotations) {
                        setRfqData(prevData => ({
                            ...prevData,
                            suppliers: prevData.suppliers?.map((supplier, index) =>
                                index === 0 ? { ...supplier, vendorQuotations: response.vendorQuotations } : supplier
                            ) || []
                        }));
                    }
                } catch (error) {
                    const unavailableMessage = getQuotationUnavailableMessage(error);
                    if (unavailableMessage) {
                        setQuotationUnavailableMessage(unavailableMessage);
                        return;
                    }
                    console.error("Error fetching vendor quotation:", error);
                }
            }
        };

        fetchVendorQuotation();
    }, [supplierId, isEdit, rfqData?.rfqId]);

    useEffect(() => {

        const latestQuotationSet = quotationIndex !== undefined ? rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.[quotationIndex] : rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0];

        if (rfqData?.suppliers?.[0]?.vendorQuotations) {
            setTotalProposal(quotationIndex !== undefined ? (latestQuotationSet?.othersTotalWithGst + latestQuotationSet?.totalWithGst) : rfqData.suppliers[0].vendorQuotations.grandTotalWithGst || 0);
            setIsCounterTerm(latestQuotationSet?.agreedToPaymentTerms === false);
            setSelectedFilesBase(latestQuotationSet?.attachments || []);
            setOverallTotalExclGST(rfqData?.suppliers?.[0]?.vendorQuotations?.grandTotalWithoutGst)

            const otherCharges = latestQuotationSet?.otherCharges || {};

            setMiscellaneousCosts({
                mathadeCharges: otherCharges?.mathadeCharges || { mrp: 0, gst: 0, totalCost: 0 },
                miscCharges: otherCharges?.miscCharges || { mrp: 0, gst: 0, totalCost: 0 }
            });
        } else {
            setMiscellaneousCosts({
                mathadeCharges: { mrp: 0, gst: 0, totalCost: 0 },
                miscCharges: { mrp: 0, gst: 0, totalCost: 0 }
            });
        }
    }, [rfqData, quotationIndex]);

    const formInitialValues = useMemo(() => {
        const selectedQuotationSet =
            quotationIndex !== undefined
                ? rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.[quotationIndex]
                : rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0];

        const rfqLabourDetails = rfqData?.labourDetails || [];
        const quotationLabourItems = selectedQuotationSet?.labourItems || [];

        // Create maps for quick lookup by ID and skill
        const quotationLabourMap = {
            byId: new Map(quotationLabourItems.map(item => [item.id, item])),
            bySkill: new Map(quotationLabourItems.map(item => [item.skill?.toLowerCase(), item])),
        };

        // Sync labour details with quotation data
        const syncedLabourItems = rfqLabourDetails.map((labourDetail) => {
            const matchingQuotationItem =
                quotationLabourMap.byId.get(labourDetail._id?.toString()) || // Match by ID
                quotationLabourMap.bySkill.get(labourDetail.skill?.toLowerCase()); // Fallback to skill

            // If there's a matching item in the quotation, merge the data
            if (matchingQuotationItem) {
                return {
                    id: matchingQuotationItem.id || labourDetail._id,
                    skill: matchingQuotationItem.skill || labourDetail.skill || "",
                    quantity: matchingQuotationItem.quantity || labourDetail.quantity || "",
                    buyerRemarks: labourDetail.remark || "",
                    remarks: matchingQuotationItem.remarks || "",
                    labourRate: matchingQuotationItem.labourRate || "",
                    gst: matchingQuotationItem.gst || 0,
                    workStartDate: matchingQuotationItem.workStartDate
                        ? new Date(matchingQuotationItem.workStartDate).toISOString().split("T")[0]
                        : rfqData?.providingDate
                            ? new Date(rfqData?.providingDate).toISOString().split("T")[0]
                            : "",
                    attachments: matchingQuotationItem.attachments || [],
                    totalAmount: matchingQuotationItem.totalAmount || 0,
                    totalExclGst: matchingQuotationItem.priceWithoutGst || 0,
                };
            }

            // If no matching item, initialize with default values
            return {
                id: labourDetail._id,
                skill: labourDetail.skill || "",
                quantity: labourDetail.quantity || "",
                buyerRemarks: labourDetail.remark || "",
                remarks: "",
                labourRate: "",
                gst: 18,
                workStartDate: rfqData?.providingDate
                    ? new Date(rfqData?.providingDate).toISOString().split("T")[0]
                    : "",
                attachments: [],
                totalAmount: 0,
                totalExclGst: 0,
            };
        });

        return {
            labourItems: syncedLabourItems,
            agreedToPaymentTerms: selectedQuotationSet?.agreedToPaymentTerms ?? true,
            counterPaymentTerms: selectedQuotationSet?.counterPaymentTerms || "",
            notes: selectedQuotationSet?.notes || "",
            rfqId: rfqData?.rfqId,
            rfqType: rfqData?.rfqType,
            supplierId: rfqData?.suppliers?.[0]?.supplierId,
            submittedBy: userProfile?.name,
            userId: userProfile?.userId,
            miscellaneousCosts: {
                mathadeCharges: {
                    mrp: selectedQuotationSet?.otherCharges?.mathadeCharges?.mrp || 0,
                    gst: selectedQuotationSet?.otherCharges?.mathadeCharges?.gst || 18,
                    totalCost: selectedQuotationSet?.otherCharges?.mathadeCharges?.totalCost || 0,
                    remark: selectedQuotationSet?.otherCharges?.mathadeCharges?.remark
                },
                miscCharges: {
                    mrp: selectedQuotationSet?.otherCharges?.miscCharges?.mrp || 0,
                    gst: selectedQuotationSet?.otherCharges?.miscCharges?.gst || 18,
                    totalCost: selectedQuotationSet?.otherCharges?.miscCharges?.totalCost || 0,
                    remark: selectedQuotationSet?.otherCharges?.miscCharges?.remark
                },
            },
        };
    }, [rfqData, quotationIndex, userProfile]);



    // State management
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [totalProposal, setTotalProposal] = useState(rfqData?.suppliers?.[0]?.vendorQuotations?.grandTotalWithGst || 0);
    const [, setIsCounterTerm] = useState(rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0]?.agreedToPaymentTerms === false);
    const [attachmentsSkeleton, setAttachmentsSkeleton] = useState([]);
    const [selectedFilesBase, setSelectedFilesBase] = useState(rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0]?.attachments || []);
    const [selectedFilesTotalSize, setSelectedFilesTotalSize] = useState([]);
    const [attachmentsUploadsErrors, setAttachmentsUploadsErrors] = useState('');
    const [openFilesSizeError, setOpenFilesSizeError] = useState(false);
    const [openAccordian, setOpenAccordian] = useState(null); // State to manage open accordion
    const [labourFilesBase, setLabourFilesBase] = useState([]);
    const [labourFilesSkeleton, setLabourFilesSkeleton] = useState([]);
    const [labourFilesTotalSize, setLabourFilesTotalSize] = useState([]);
    const [labourUploadsErrors, setLabourUploadsErrors] = useState("");
    const [openLabourFileSizeError, setOpenLabourFileSizeError] = useState(false);
    const [openUploadFile, setOpenUploadFile] = useState(false);
    const [selectedLabourIndex, setSelectedLabourIndex] = useState(null);
    const [overallTotalExclGST, setOverallTotalExclGST] = useState(0);
    const [expandedStates, setExpandedStates] = useState({}); // Track expanded states for each item
    const [sectionCheckboxes, setSectionCheckboxes] = useState({});
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [submissionValues, setSubmissionValues] = useState(null);
    const toggleExpanded = (index) => {
        setExpandedStates((prev) => ({
            ...prev,
            [index]: !prev[index], // Toggle specific item's state
        }));
    };

    const [miscellaneousCosts, setMiscellaneousCosts] = useState({
        mathadeCharges: { mrp: 0, gst: 0, totalCost: 0, remark: "" },
        miscCharges: { mrp: 0, gst: 0, totalCost: 0, remark: "" }
    });

    const miscellaneousItems = [
        { label: "Mathade Charges", name: "mathadeCharges" },
        { label: "Miscellaneous Charges", name: "miscCharges" },
    ];

    const { otherCharges } = rfqData || {};

    const visibleMiscellaneousItems = miscellaneousItems.filter(item => otherCharges?.[item.name]);

    // Check if any items are visible
    const hasVisibleMiscellaneousItems = visibleMiscellaneousItems.length > 0;

    const currentRank = rfqData?.suppliers[0]?.vendorQuotations?.rank
    // console.log(currentRank)

    const attachmentsData = rfqData?.selectedFilesBase
        ? rfqData.selectedFilesBase.map((item) => {
            const lastIndex = item.Name.lastIndexOf('.'); // Extract file extension
            const path = lastIndex !== -1 ? item.Name.substring(lastIndex + 1) : null;
            return { ...item, Path: path }; // Add the Path to determine file type
        })
        : [];



    // Mock data for rows
    const rows = [
        { name: 'Shuttering', value: '60', price: 'Rs. 0', remarks: "1 remark" },
        { name: 'Fitters', value: '60', price: 'Rs. 0', remarks: "2 remark" },
        // Add more rows as needed
    ];

    const attachedDocument = [
        { label: "HCIM/CBIS Bar-FE500 Disk", imgSrc: "" },
        { label: "HCIM/CBIS Bar-FE500 Disk", imgSrc: "" },
        { label: "HCIM/CBIS Bar-FE500 Disk", imgSrc: "" },
        { label: "HCIM/CBIS Bar-FE500 Disk", imgSrc: "" },
        { label: "HCIM/CBIS Bar-FE500 Disk", imgSrc: "" },
    ];

    const toggleAccordion = (index) => {
        setOpenAccordian(openAccordian === index ? null : index); // Toggle the open state
    };

    // Yup validation schema
    const miscellaneousSchema = Yup.object().shape({
        mrp: Yup.number()
            .typeError("Enter a valid MRP")
            .required("MRP is required")
            .min(0, "Invalid MRP"),
        gst: Yup.number()
            .typeError("Enter a valid GST")
            .required("GST is required")
            .min(0, "Invalid GST")
    });

    const validationSchema = Yup.object().shape({
        labourItems: Yup.array().of(
            Yup.object().shape({
                labourRate: Yup.number().typeError('Rate should be number').required('Rate is required').min(0, 'Invalid rate'),
                gst: Yup.number().typeError('Enter a valid GST').required('GST is required').min(0, 'Invalid GST'),
            })
        ),

        miscellaneousCosts: Yup.object().shape({
            // transport: miscellaneousSchema,
            // loading: miscellaneousSchema,
            mathadeCharges: miscellaneousSchema,
            miscCharges: miscellaneousSchema,
        }),
    });

    const handleSubmitWithValidation = async (validateForm, setTouched, values, submitForm) => {
        const validationErrors = await validateForm();
        const firstLabourErrorIndex = validationErrors.labourItems?.findIndex(Boolean) ?? -1;

        if (firstLabourErrorIndex >= 0) {
            setOpenAccordian(firstLabourErrorIndex);
            setTouched({
                labourItems: values.labourItems.map((_, index) =>
                    index === firstLabourErrorIndex ? { labourRate: true, gst: true } : {}
                ),
            });
            enqueueSnackbar('Please fill the required labour details.', {
                variant: 'warning',
                autoHideDuration: 2000,
            });
            return;
        }

        submitForm();
    };

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes


    // Utility function to validate file size
    const isFileSizeValid = (files, existingSize) => {
        const sizeOfSelectedFiles = Array.from(files).reduce((acc, file) => acc + file.size, 0);
        const totalSize = sizeOfSelectedFiles + existingSize;
        return totalSize < MAX_FILE_SIZE;
    };

    // Utility function to read files and convert them to base64
    const readAndConvertFiles = (files) => {
        return Array.from(files).map((file) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const fileContent = e.target.result;
                    const encryptedContent = btoa(fileContent); // Encrypt in Base64
                    let contentType = file.type;
                    if (!contentType && file.name.endsWith(".dwg")) {
                        contentType = "application/acad"; // Handle .dwg files
                    }
                    resolve({
                        Name: file.name,
                        Content: encryptedContent,
                        ContentType: contentType,
                        FileSize: file.size,
                    });
                };
                reader.readAsBinaryString(file);
            });
        });
    };

    // Handle file upload click for clearing input on click
    const handleLabourFileUploadClick = (event) => {
        event.target.value = ""; // Clear file input on click
    };

    // Handle closing the file size error
    const handleCloseLabourFileSizeError = () => {
        setOpenLabourFileSizeError(false);
    };

    const handleLabourFileUpload = (event, labourIndex, setFieldValue, values) => {
        const files = event.target.files;
        const totalExistingSize = labourFilesTotalSize.reduce((acc, file) => acc + file.FileSize, 0);

        if (isFileSizeValid(files, totalExistingSize)) {
            setLabourFilesSkeleton((prevVal) => [...prevVal, ...Array.from(files)]);

            const convertedFilesPromises = readAndConvertFiles(files);

            Promise.all(convertedFilesPromises)
                .then(async (convertedFiles) => {
                    try {
                        const response = await axios.post("/api/rfqs/upload/attachments", convertedFiles);

                        const currentAttachments = values.labourItems[labourIndex]?.attachments || [];
                        const updatedAttachments = [...currentAttachments, ...response.data];

                        setFieldValue(`labourItems[${labourIndex}].attachments`, updatedAttachments);
                        setLabourFilesSkeleton([]);
                    } catch (error) {
                        setLabourFilesSkeleton([]);
                        setLabourUploadsErrors(error.message);
                        setTimeout(() => setLabourUploadsErrors(""), 5000);
                    }
                })
                .catch((error) => {
                    console.error("Error reading files", error);
                });
        } else {
            setOpenLabourFileSizeError(true);
            setTimeout(() => setOpenLabourFileSizeError(false), 6000);
        }
    };

    const handleDeleteLabourFile = (name, event, setFieldValue, selectedLabourIndex, values) => {
        event.preventDefault();

        // Update local state to remove the file from the labourFilesBase array
        setLabourFilesBase((prevVal) => prevVal.filter((file) => file.Name !== name));
        setLabourFilesTotalSize((prevVal) => prevVal.filter((file) => file.Name !== name));

        // Update Formik state to remove the file from the specific labour item
        setFieldValue(
            `labourItems[${selectedLabourIndex}].attachments`,
            values.labourItems[selectedLabourIndex].attachments.filter((file) => file.Name !== name)
        );
    };



    // Event handlers
    const togglePopup = () => {
        setIsPopupOpen(!isPopupOpen);
    };

    const handleCloseForm = () => {
        if (supplierId) {
            navigate('/leads')
        } else {
            navigate(-1)
        }
    }


    // Helper functions
    const calculateTotalAmount = (index, rate, gst, labourItems) => {
        const strength = parseFloat(labourItems[index].quantity);
        const ratePerLabour = rate ? parseFloat(rate) : 0;
        const gstPercent = gst ? parseFloat(gst) : 0;
        return strength * ratePerLabour * (1 + gstPercent / 100);
    };

    const calculateTotalExclGst = (index, rate, labourItems) => {
        const strength = parseFloat(labourItems[index].quantity);
        const ratePerLabour = rate ? parseFloat(rate) : 0;
        return strength * ratePerLabour;
    };

    const calculateOverallTotal = (labourItems, miscCosts) => {
        const lineItemsTotal = labourItems.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
        const miscCostsTotal = Object.values(miscCosts).reduce((sum, item) => sum + (item.totalCost || 0), 0);
        return lineItemsTotal + miscCostsTotal;
    };

    const calculateOverallTotalWithoutGST = (labourCosts, miscCosts) => {
        const totallabourCostsExclGST = labourCosts.reduce((acc, curr) => acc + curr.totalExclGst, 0);
        const totalMiscCostsExclGST = Object.values(miscCosts).reduce((acc, curr) => acc + (curr.mrp || 0), 0);
        const newOverallTotalExclGST = totallabourCostsExclGST + totalMiscCostsExclGST;
        setOverallTotalExclGST(newOverallTotalExclGST);
    };

    const handleLabourRateChange = (index, value, setFieldValue, labourItems) => {
        const rate = parseFloat(value) || 0;
        const gst = parseFloat(labourItems[index].gst || 0);
        const quantity = parseFloat(labourItems[index].quantity || 0);

        const totalExclGst = quantity * rate;
        const totalAmount = totalExclGst * (1 + gst / 100);

        setFieldValue(`labourItems[${index}].labourRate`, value);
        setFieldValue(`labourItems[${index}].totalExclGst`, totalExclGst);
        setFieldValue(`labourItems[${index}].totalAmount`, totalAmount);

        const updatedLineItems = [...labourItems];
        updatedLineItems[index] = {
            ...updatedLineItems[index],
            totalExclGst,
            totalAmount,
        };

        const newTotalProposal = calculateOverallTotal(updatedLineItems, miscellaneousCosts);
        calculateOverallTotalWithoutGST(updatedLineItems, miscellaneousCosts);
        setTotalProposal(newTotalProposal);
    };


    const handleRemarkChange = (index, value, setFieldValue) => {
        setFieldValue(`labourItems[${index}].remarks`, value);
    };

    const handleGstChange = (index, value, setFieldValue, labourItems) => {
        setFieldValue(`labourItems[${index}].gst`, value);
        const totalAmount = calculateTotalAmount(index, labourItems[index].labourRate, value, labourItems);
        setFieldValue(`labourItems[${index}].totalAmount`, totalAmount);

        const updatedLineItems = [...labourItems];
        updatedLineItems[index].totalAmount = totalAmount;
        const newTotalProposal = calculateOverallTotal(updatedLineItems, miscellaneousCosts);
        setTotalProposal(newTotalProposal);
        calculateOverallTotalWithoutGST(updatedLineItems, miscellaneousCosts);

    };

    const calculateTransportationTotalCost = (mrp, gst) => {
        const gstValue = parseFloat(gst) || 0;
        const mrpValue = parseFloat(mrp) || 0;
        return mrpValue + (mrpValue * gstValue) / 100;
    };

    const handleMiscellaneousChange = (field, value, setFieldValue, values, type) => {

        if (type === "remark") {
            const updatedMiscellaneousCosts = { ...values.miscellaneousCosts };
            updatedMiscellaneousCosts[field].remark = value;  // Update the remark value.
            // Set the field value for 'remark' in form state
            setFieldValue(`miscellaneousCosts.${field}.remark`, value);
            setMiscellaneousCosts(updatedMiscellaneousCosts);
            const newTotalProposal = calculateOverallTotal(values.labourItems, updatedMiscellaneousCosts);
            setTotalProposal(newTotalProposal);
            calculateOverallTotalWithoutGST(values.labourItems, updatedMiscellaneousCosts);

            // console.log("Updated miscellaneousCosts", updatedMiscellaneousCosts);
        } else {
            const parsedValue = value === "" ? "" : parseFloat(value) || 0;
            const updatedMiscellaneousCosts = { ...values.miscellaneousCosts };
            updatedMiscellaneousCosts[field][type] = parsedValue;

            if (updatedMiscellaneousCosts[field].mrp !== "" && updatedMiscellaneousCosts[field].gst !== "") {
                updatedMiscellaneousCosts[field].totalCost = calculateTransportationTotalCost(
                    updatedMiscellaneousCosts[field].mrp,
                    updatedMiscellaneousCosts[field].gst
                );
            } else {
                updatedMiscellaneousCosts[field].totalCost = 0;
            }

            setFieldValue(`miscellaneousCosts.${field}.${type}`, parsedValue);
            setMiscellaneousCosts(updatedMiscellaneousCosts);
            const newTotalProposal = calculateOverallTotal(values.labourItems, updatedMiscellaneousCosts);
            setTotalProposal(newTotalProposal);
            calculateOverallTotalWithoutGST(values.labourItems, updatedMiscellaneousCosts);
        }

    };

    const handleMiscGstChange = (field, value, setFieldValue, values) => {
        handleMiscellaneousChange(field, value, setFieldValue, values, 'gst');
    };


    const handleFileUploadClick = (event) => {
        event.target.value = '';
    };

    const handleFileUpload = (event) => {
        const files = event.target.files;
        const sizeOfSelectedFiles = Array.from(files).reduce((acc, obj) => acc + obj.size, 0);
        const sizeOfAllSelectedFiles = selectedFilesTotalSize.reduce((acc, obj) => acc + obj.FileSize, sizeOfSelectedFiles);

        if (sizeOfAllSelectedFiles < MAX_FILE_SIZE) {
            setAttachmentsSkeleton((prevVal) => [...prevVal, ...Array.from(files)]);

            const convertedFilesPromises = Array.from(files).map((file) => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        const fileContent = e.target.result;
                        const encryptedContent = btoa(fileContent); // Encrypt file content in Base64
                        let contentType = file.type;
                        if (!contentType && file.name.endsWith('.dwg')) {
                            contentType = 'application/acad'; // MIME type for DWG files
                        }
                        const payload = {
                            Name: file.name,
                            Content: encryptedContent,
                            ContentType: contentType,
                            FileSize: file.size,
                        };
                        resolve(payload);
                    };
                    reader.readAsBinaryString(file);
                });
            });

            Promise.all(convertedFilesPromises)
                .then((convertedFiles) => attachmentBlobLink(convertedFiles))
                .catch((error) => console.error(error));
        } else {
            setOpenFilesSizeError(true);
            setTimeout(() => setOpenFilesSizeError(false), 6000);
        }
    };

    const handleDeleteSelectedFile = (name, event) => {
        event.preventDefault();
        setSelectedFilesBase((prevVal) => prevVal.filter((data) => data.Name !== name));
        setSelectedFilesTotalSize((prevVal) => prevVal.filter((data) => data.Name !== name));
    };

    const handleCloseFileSizeError = () => {
        setOpenFilesSizeError(false);
    };

    const attachmentBlobLink = (convertedFiles) => {
        axios.post('/api/rfqs/upload/attachments', convertedFiles)
            .then((response) => {
                setSelectedFilesBase((prevVal) => [...prevVal, ...response.data]);
                setSelectedFilesTotalSize((prevVal) => [...prevVal, ...convertedFiles]);
                setAttachmentsSkeleton([]);
            })
            .catch((error) => {
                setAttachmentsSkeleton([]);
                setAttachmentsUploadsErrors(error.message);
                setTimeout(() => setAttachmentsUploadsErrors(''), 5000);
                console.error(error);
            });
    };

    // Handle individual labour item checkbox change
    const handleLabourCheckboxChange = (index, checked) => {
        setSectionCheckboxes(prev => ({
            ...prev,
            [index]: checked
        }));
    };

    // Check if all sections are checked
    const areAllSectionsChecked = () => {
        return Object.values(sectionCheckboxes).every(Boolean);
    };

    const stripHtml = (html) => {
        const temp = document.createElement("div");
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || "";
    };

    // Initialize section checkboxes for labour items
    useEffect(() => {
        if (rfqData?.labourDetails) {
            const initialCheckboxes = {};
            rfqData.labourDetails.forEach((_, index) => {
                initialCheckboxes[index] = true; // All checked by default
            });
            setSectionCheckboxes(initialCheckboxes);
        }
    }, [rfqData?.labourDetails]);

    useEffect(() => {
        if (!loading && error) {
            // Show error snackbar when there's an error
            enqueueSnackbar(`Error: ${errorMessage}`, {
                variant: 'error',
                autoHideDuration: 3000,
            });
        }
    }, [loading, error, errorMessage, enqueueSnackbar]);
        const submitQuotationPayload = async (payload) => {
        try {
            await dispatch(submitLabourQuotation({ values: payload }));
            if (isMobileQuotationView()) {
                enqueueSnackbar("Quotation submitted successfully!", {
                    variant: "success",
                    autoHideDuration: 3000,
                });
            }
            if (supplierId) {
                navigate("/leads");
            } else {
                navigate(-1);
            }
        } catch (error) {
            console.error("Submission failed:", error);
            enqueueSnackbar("Submission failed. Please try again.", {
                variant: "error",
                autoHideDuration: 3000,
            });
        }
    };
    const buildQuotationUpdateReason = (remark) => ({ remark });

    const handleQuotationUpdateReasonSubmit = async (remark) => {
        if (!pendingQuotationPayload) return;
        await submitQuotationPayload({
            ...pendingQuotationPayload,
            quotationUpdateReason: buildQuotationUpdateReason(remark)
        });
        setPendingQuotationPayload(null);
        setShowQuotationUpdateReasonModal(false);
    };
    if (quotationUnavailableMessage) {
        return <RfqUnavailableNotice message={quotationUnavailableMessage} />;
    }
    return (
        <div className="w-full min-h-full bg-gray-100 shadow-md" style={{ fontFamily: "goldman_sans" }}>

            <Formik
                enableReinitialize={true}
                initialValues={formInitialValues}
                validationSchema={validationSchema}
                onSubmit={async (values) => {
                    // const payload = { ...values, selectedFilesBase, otherCharges: values.miscellaneousCosts };
                    // try {
                    //     await dispatch(submitLabourQuotation({ values: payload }));
                    //     if (supplierId) {
                    //         navigate('/leads')
                    //     } else {
                    //         navigate(-1)
                    //     }
                    // } catch (error) {
                    //     console.error("Submission failed:", error);
                    // }
                    setSubmissionValues(values);
                    setShowConfirmPopup(true);
                    return;
                }}
            >
                {({ values, setFieldValue, validateForm, setTouched, submitForm }) => (
                    <Form
                        autoComplete="off"
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
                        <QuotationTopNav showRank={shouldShowVendorRank(rfqData)} currentRank={currentRank} />
                        <div className="quotation-pdf-ignore flex justify-end">
                            <QuotationPdfPreviewButton targetRef={quotationPdfRef} rfqData={rfqData} />
                        </div>
                        <div ref={quotationPdfRef}>
                        {/*Mobile View */}
                        <div className="lg:hidden h-screen flex-1 overflow-y-auto bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF]">

                            {/* mobile content */}
                            <div className="h-auto flex-grow overflow-y-auto">
                                <div className="relative step-content min-h-full">
                                    {(
                                        <div className="px-4 pt-4">

                                            {false && shouldShowVendorRank(rfqData) && <div className="mb-3 rounded-2xl border border-[#DDEFD9] bg-white p-3 shadow-sm flex flex-col items-center justify-center">
                                                <div className="flex items-center space-x-2">
                                                    <span class="relative flex items-center justify-center h-4 w-4 ">
                                                        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-500 opacity-40"></span>
                                                        <span class="relative inline-flex rounded-full h-2 w-2 bg-lime-500"></span>
                                                    </span>
                                                    <h3 className="text-sm lg:mt-1 text-center text-[#737373]">
                                                        Quotation Rank:{" "}
                                                        <span className="font-bold text-black">
                                                            {currentRank === "L1"
                                                                ? "L2"
                                                                : currentRank === undefined || currentRank === null
                                                                    ? "Quotation not submitted"
                                                                    : currentRank}
                                                        </span>
                                                    </h3>
                                                    {/* <p className="text-[#9F9F9F] lg:mt-1 text-xs italic hidden lg:block">Your rank will update as soon as the buyer receives atleast 3 ratings</p> */}
                                                </div>

                                                <span className="pt-1 text-[#737373] text-xs text-center group-hover:block">
                                                    {currentRank === undefined || currentRank === null
                                                        ? "Quotation rank is available after quotation submission"
                                                        : "Based on your submitted quotation"}
                                                </span>
                                            </div>}
                                            {/* rfq information */}
                                            {/* <div className="px-4 lg:py-[24px] lg:px-8">
                                                <div className=" mt-4 mb-1 lg:hidden flex justify-between space-x-10">
                                                    <h3 className="text-sm font-semibold text-[#3B3B3B]"><span className="text-[#787878]">Requirement Name:</span> {rfqData?.name}</h3>
                                                </div>
                                                <p className="text-sm font-normal text-[#787878] lg:hidden">Create Date: {new Date(rfqData?.createdAt).toLocaleDateString('en-GB')}</p>

                                                <p className="text-sm font-medium text-[#787878] mt-6 lg:hidden">RFQ Information</p>

                                            </div> */}
                                            {/* rfq information */}
                                            <RfqInformation
                                                rfqData={rfqData}
                                                togglePopup={togglePopup}
                                                isPopupOpen={isPopupOpen}
                                                isAccordion
                                            />

                                        </div>
                                    )}
                                    <div className="px-4 py-5 space-y-5">
                                        {(
                                            <MobileSectionCard
                                                number="1"
                                                title={`Labour Requirements (${rfqData?.labourDetails?.length || 0})`}
                                                description="Open each labour item to submit rates and remarks."
                                            >
                                                {/* input fields */}
                                                <div>
                                                    {/* Labour */}
                                                    <div className="lg:flex items-center my-[30px] hidden">
                                                        <h3 className="whitespace-nowrap text-[#434343] uppercase text-base font-medium">Labour</h3>
                                                        <hr className="flex-grow border-t border-gray-300 ml-4" />
                                                    </div>
                                                    <div className="space-y-3 mt-4">
                                                        {rfqData?.labourDetails?.map((row, index) => (
                                                            <div key={index} className="rounded-xl border border-[#E7E7DD] bg-white overflow-hidden">
                                                                {/* Mobile Accordion Header */}
                                                                <div className={`block lg:hidden px-4 py-3 cursor-pointer ${openAccordian === index ? 'bg-[#F8FAFF]' : 'bg-white'}`} onClick={() => toggleAccordion(index)}>
                                                                    <div className="flex justify-between">
                                                                        <div className="flex items-start space-x-4 flex-1 min-w-0">
                                                                            <div className="w-[28px] h-[28px] shrink-0 bg-[#EFEFEB] rounded-full flex justify-center items-center">
                                                                                <p className="text-xs font-semibold text-[#6A6A6A]">{index + 1}</p>
                                                                            </div>

                                                                            <div className="flex flex-col flex-1 min-w-0">
                                                                                <h2 className="text-sm font-semibold leading-5 text-[#303026]">
                                                                                    {openAccordian === index ? (
                                                                                        ReactHtmlParser(row?.skill)
                                                                                    ) : (
                                                                                        <span className="block truncate">
                                                                                            {stripHtml(row?.skill)}
                                                                                        </span>
                                                                                    )}
                                                                                </h2>

                                                                                {/* Checkbox and note */}
                                                                                <div className="flex items-center space-x-2 mt-1">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={sectionCheckboxes[index] || false}
                                                                                        onChange={(e) =>
                                                                                            handleLabourCheckboxChange(index, e.target.checked)
                                                                                        }
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        className="h-4 w-4 text-blue-600 rounded cursor-pointer disabled:cursor-not-allowed shrink-0"
                                                                                        disabled={isReadOnly}
                                                                                    />
                                                                                    <p className="text-[10px] text-gray-600 truncate">
                                                                                        I have read the work description
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Dropdown icon */}
                                                                        <div className="shrink-0 ml-2 flex">
                                                                            <img
                                                                                src="https://storagereponeevaydevcdn.blob.core.windows.net/business/dropdown_arrow.svg"
                                                                                alt="dropdown"
                                                                                className={`w-4 h-4 transition-transform ${openAccordian === index ? "rotate-180" : ""
                                                                                    }`}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Accordion Content */}
                                                                {openAccordian === index && (
                                                                    <div className="space-y-4 text-sm border-t border-[#E7E7DD] bg-[#FCFCFA] py-4 px-4">
                                                                        <div>
                                                                            <label className="font-semibold text-sm col-span-2 flex items-center relative">
                                                                                Quantity
                                                                                <span className="bg-[#cac9c9] text-white text-xs h-[15px] w-[15px] flex justify-center items-center mb-1 ml-1 pt-1 rounded-full lowercase relative group hover:cursor-pointer">
                                                                                    i
                                                                                    <span className="absolute bottom-[130%] text-xs left-1/2 w-[200px] transform -translate-x-1/2 px-2 py-1 text-[#434343] text-left bg-white border border-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                                                                        Enter the cost per unit for the selected construction material. Ensure accuracy as this value impacts overall project budgeting and pricing.
                                                                                    </span>
                                                                                </span>
                                                                            </label>
                                                                            <p className=" border-none text-sm placeholder-[#373737]">
                                                                                {row?.quantity} {row?.unit}
                                                                            </p>
                                                                        </div>
                                                                        {row?.remark?.length > 0 && <div>
                                                                            <p className="text-sm font-semibold mb-2"> Remarks</p>
                                                                            <div className="bg-[#F6F6F4] max-h-[100px] overflow-y-auto col-span-2 py-2 px-4 w-full " >
                                                                                <p className="w-full border-none col-span-3 p-0 bg-[#F6F6F4] text-sm" dangerouslySetInnerHTML={{
                                                                                    __html:
                                                                                        row?.remark?.length > 100
                                                                                            ? row?.remark.slice(0, 100) + "..."
                                                                                            : row?.remark,
                                                                                }}>
                                                                                </p>
                                                                                {row?.remark?.length > 100 && <button
                                                                                    onClick={togglePopup}
                                                                                    type="button"
                                                                                    className="text-blue-600 italic text-xs underline pt-2"
                                                                                >
                                                                                    Read More
                                                                                </button>}

                                                                                {/* Popup Modal */}
                                                                                {isPopupOpen && (
                                                                                    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">

                                                                                        <div className="bg-white border-[25px] mx-4 border-white rounded-lg shadow-lg max-w-md w-full max-h-[320px] relative" style={{ overflowY: 'scroll', msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
                                                                                            <style>{` .hide-scrollbar {-ms-overflow-style: none; /* Internet Explorer 10+ */
                                                                                                        scrollbar-width: none; /* Firefox */
                                                                                                        }
                                                                                                        .hide-scrollbar::-webkit-scrollbar {
                                                                                                        display: none; /* Safari and Chrome */
                                                                                                    }
                                                                                                `}
                                                                                            </style>
                                                                                            <div className="sticky top-0 bg-white pb-4">
                                                                                                {/* Close Button */}
                                                                                                <button
                                                                                                    onClick={togglePopup}
                                                                                                    className="absolute top-0 right-0 text-gray-400 hover:text-gray-600"
                                                                                                >
                                                                                                    <svg className="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                                                                    </svg>
                                                                                                </button>
                                                                                                <h2 className="text-sm font-semibold mb-2"> Remarks</h2>
                                                                                                <hr />
                                                                                            </div>
                                                                                            <p className="text-sm text-[#3D3D3D] overflow-y-auto " dangerouslySetInnerHTML={{
                                                                                                __html: row?.remark
                                                                                            }}>
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>}
                                                                        {/* rate per labourer per day */}
                                                                        <div>
                                                                            <label className="font-semibold text-sm pb-2 col-span-2 flex items-center relative">
                                                                                Rate
                                                                                <span className="bg-[#cac9c9] text-white text-xs h-[15px] w-[15px] flex justify-center items-center mb-1 ml-1 pt-1 rounded-full lowercase relative group hover:cursor-pointer">
                                                                                    i
                                                                                    <span className="absolute bottom-[130%] text-xs left-1/2 w-[200px] transform -translate-x-1/2 px-2 py-1 text-[#434343] text-left bg-white border border-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                                                                        Enter the cost per unit for the selected construction material. Ensure accuracy as this value impacts overall project budgeting and pricing.
                                                                                    </span>
                                                                                </span>
                                                                            </label>
                                                                            <Field
                                                                                name={`labourItems[${index}].labourRate`}
                                                                                type="text"
                                                                                className={`h-[40px] rounded-lg border-[#DCDCD0] border px-3 py-2 text-sm ${isReadOnly ? "bg-gray-100" : "bg-white"}`}
                                                                                onChange={e => handleLabourRateChange(index, e.target.value, setFieldValue, values.labourItems)}
                                                                                disabled={isReadOnly}
                                                                            />
                                                                            <ErrorMessage name={`labourItems[${index}].labourRate`} component="div" className="text-red-500 text-sm" />
                                                                        </div>
                                                                        <div className="">
                                                                            {/* gst */}
                                                                            <div>
                                                                                <label htmlFor="" className="text-sm pb-2 font-semibold lg:hidden">GST (%)</label>
                                                                                <CustomGstDropdown
                                                                                    options={[0, 5, 12, 18, 28]}
                                                                                    // Display the current value
                                                                                    value={values.labourItems[index]?.gst || ''}
                                                                                    onChange={value => handleGstChange(index, value, setFieldValue, values.labourItems)}
                                                                                    className="h-[40px] w-1/3 justify-between"
                                                                                    disabled={isReadOnly}
                                                                                />
                                                                                {/* <ErrorMessage name={`labourItems[${index}].gst`} component="div" className="text-red-500 text-sm h-fit" /> */}
                                                                            </div>

                                                                            {/* work start date */}
                                                                            {/* <div className="self-end ">
                                                                            <label className="font-semibold text-sm pb-2 col-span-2 flex items-center relative">
                                                                                Work Start Date
                                                                                <span className="bg-[#cac9c9] text-white text-xs h-[15px] w-[15px] flex justify-center items-center mb-1 ml-1 pt-1 rounded-full lowercase relative group hover:cursor-pointer">
                                                                                    i
                                                                                    <span className="absolute bottom-[130%] text-xs left-1/2 w-[200px] transform -translate-x-1/2 px-2 py-1 text-[#434343] text-left bg-white border border-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                                                                        Enter the cost per unit for the selected construction material. Ensure accuracy as this value impacts overall project budgeting and pricing.
                                                                                    </span>
                                                                                </span>
                                                                            </label>
                                                                            <Field
                                                                                type="date"
                                                                                name={`labourItems[${index}].workStartDate`}
                                                                                className="p-2 h-[40px] text-xs border border-gray-300 placeholder-[#373737]"
                                                                            />
                                                                            <ErrorMessage name={`labourItems[${index}].workStartDate`} component="div" className="text-red-500 text-xs absolute" />
                                                                        </div> */}
                                                                            <div className=" flex justify-start space-x-4 items-center text-center col-span-1 mt-2">
                                                                                <p className="text-sm pb-2 font-semibold ">Add Attachment</p>
                                                                                <div className="relative">
                                                                                    <AttachFileIcon
                                                                                        className="hover:cursor-pointer"
                                                                                        sx={{
                                                                                            width: "0.7em", ml: "2px", mb: "8px",
                                                                                            transform: 'rotate(45deg)',
                                                                                            transition: 'transform 0.3s ease'
                                                                                        }}
                                                                                        onClick={() => {
                                                                                            setSelectedLabourIndex(index); // Set the labor index
                                                                                            setOpenUploadFile(true); // Open the file upload popup
                                                                                        }}
                                                                                    />

                                                                                    {values.labourItems[index]?.attachments?.length > 0 && (
                                                                                        <span
                                                                                            className="absolute top-0 right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
                                                                                            style={{ transform: 'translate(50%, -50%)' }}
                                                                                        >
                                                                                            {values.labourItems[index].attachments.length}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <div>
                                                                            <label htmlFor="" className="text-sm pb-2 font-semibold lg:hidden">Remarks</label>
                                                                            <Field
                                                                                name={`labourItems[${index}].remarks`}
                                                                                type="text"
                                                                                className={`w-full h-[40px] rounded-lg text-xs border-[#DCDCD0] border px-3 py-2 ${isReadOnly ? "bg-gray-100" : "bg-white"}`}
                                                                                placeholder=""
                                                                                onChange={e => handleRemarkChange(index, e.target.value, setFieldValue)}
                                                                                disabled={isReadOnly}
                                                                            />
                                                                            <ErrorMessage name={`labourItems[${index}].remarks`} component="div" className="text-red-500 text-xs absolute" />
                                                                        </div>
                                                                        {/* amount */}
                                                                        <div className="flex justify-between lg:justify-end items-center p-4 bg-[#E7EAEF] border border-t-[#004491] lg:border-none lg:bg-white">
                                                                            <p className="text-[#004491] text-sm font-semibold lg:hidden">Amount</p>
                                                                            <p className="text-[#004491] font-semibold text-sm">
                                                                                Rs. {formatAmount(values.labourItems[index].totalExclGst)}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </MobileSectionCard>
                                        )}
                                        {(
                                            <MobileSectionCard
                                                number="2"
                                                title="Additional Cost Components"
                                                description="Add other charges only if they are applicable."
                                            >
                                                {/* miscellaneous */}
                                                <div>
                                                    <div className=" text-xs pb-4 ">
                                                        {(() => {
                                                            const chargeLabels = {
                                                                // transport: "transport",
                                                                mathadeCharges: "mathade",
                                                                // loading: "loading",
                                                                // unLoading: "unloading",
                                                                miscCharges: "miscellaneous",
                                                            };

                                                            const absentCharges = Object.entries(rfqData?.otherCharges || {})
                                                                .filter(([_, value]) => value === false)
                                                                .map(([key]) => chargeLabels[key])
                                                                .filter(Boolean);

                                                            if (absentCharges.length === 0) return null;

                                                            const formattedCharges =
                                                                absentCharges.length === 1
                                                                    ? absentCharges[0]
                                                                    : absentCharges.slice(0, -1).join(", ") + " and " + absentCharges.slice(-1);

                                                            const isPlural = absentCharges.length > 1;

                                                            return (
                                                                <p>
                                                                    <b> IMPORTANT NOTE:</b> Please note that if {formattedCharges} charges are applicable,
                                                                    {isPlural ? " they must" : " it must"} be included in the rate and applicable charges will
                                                                    be taken care by vendor unless mentioned in the terms otherwise.
                                                                </p>
                                                            );
                                                        })()}
                                                    </div>
                                                    <div className="border lg:border-none shadow-md px-3 pb-3 pt-4 space-y-4 lg:space-y-2">
                                                        {hasVisibleMiscellaneousItems ? (
                                                            visibleMiscellaneousItems.map((item, index) => (
                                                                <div key={index} className="lg:grid lg:grid-cols-6 lg:gap-4 ">
                                                                    <React.Fragment>
                                                                        {/* Mobile view: MRP input field and GST dropdown */}
                                                                        <div className="flex justify-between items-center lg:hidden pb-2">
                                                                            <div>
                                                                                <p className="col-span-3 text-sm pb-1 lg:pb-0">{item.label}</p>
                                                                                <Field
                                                                                    name={`miscellaneousCosts.${item.name}.mrp`}
                                                                                    type="text"
                                                                                    className={`h-[40px] rounded-lg text-sm border border-gray-300 px-3 ${isReadOnly ? "bg-gray-100" : "bg-white"}`}
                                                                                    placeholder="Rs."
                                                                                    value={values.miscellaneousCosts[item.name]?.mrp || ''}
                                                                                    onChange={(e) => handleMiscellaneousChange(item.name, e.target.value, setFieldValue, values, 'mrp')}
                                                                                    disabled={isReadOnly}
                                                                                />
                                                                                <ErrorMessage name={`miscellaneousCosts.${item.name}.mrp`} component="div" className="text-red-500" />
                                                                            </div>
                                                                            <div>
                                                                                <p className="text-sm pb-1">GST</p>
                                                                                <CustomGstDropdown
                                                                                    options={[0, 5, 12, 18, 28]}
                                                                                    value={values.miscellaneousCosts[item.name]?.gst}
                                                                                    onChange={(value) => handleMiscGstChange(item.name, value, setFieldValue, values)}
                                                                                    className="col-span-1 h-[40px] w-[64px]"
                                                                                    disabled={isReadOnly}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div>
                                                                            <p className="text-sm pb-1">Remarks</p>
                                                                            <ReactQuill
                                                                                value={values.miscellaneousCosts[item.name]?.remark || ''}
                                                                                name={`miscellaneousCosts.${item.name}.remark`}
                                                                                onChange={(value) =>
                                                                                    handleMiscellaneousChange(item.name, value, setFieldValue, values, "remark")
                                                                                }
                                                                                disabled={isReadOnly}
                                                                                theme="snow"
                                                                                className="min-h-[30px] max-h-[120px] mb-2 text-xs word-break p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white border-[#DCDCD0] border w-full overflow-y-auto remove-quill-padding"
                                                                                modules={{ toolbar: false }}
                                                                            />
                                                                            <ErrorMessage
                                                                                name={`miscellaneousCosts.${item.name}.remark`}
                                                                                component="div"
                                                                                className="text-red-500 text-xs"
                                                                            />
                                                                        </div>

                                                                        {/* Subtotal */}
                                                                        <div className="flex justify-between lg:justify-end items-center text-sm py-2 px-4 bg-[#E7EAEF] border border-t-[#004491] lg:border-none lg:bg-white">
                                                                            <p className="text-[#004491] font-semibold lg:hidden">Sub Total</p>
                                                                            <p className="text-[#004491] font-semibold lg:font-normal col-span-1 lg:text-black lg:text-right">
                                                                                Rs. {formatAmount(values.miscellaneousCosts[item.name]?.totalCost)}
                                                                            </p>
                                                                        </div>
                                                                    </React.Fragment>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            // Message when hasVisibleMiscellaneousItems is false
                                                            <div className="text-center text-sm text-[#9D9D9D] py-4">
                                                                No other charges are being accepted by the buyer. The total quotation amount is:
                                                            </div>
                                                        )}

                                                        {/* Total Amount */}
                                                        <div className="lg:hidden">
                                                            <div className="flex px-4 py-2 text-sm lg:p-0 justify-between items-center border lg:border-none border-t-[#007366] bg-[#E0F0EE] lg:bg-white">
                                                                <p className="text-[#007366] lg:text-black font-semibold">Total Basic Amount</p>
                                                                <p className="bg-[#E0F0EE] text-[#007366] font-semibold text-end lg:font-normal lg:text-black w-fit min-w-[164px] lg:px-4 py-2">
                                                                    Rs. {formatAmount(overallTotalExclGST)}
                                                                </p>
                                                            </div>

                                                            {/* GST Amount Section */}
                                                            <div className="">
                                                                <hr className="hidden lg:block" />
                                                                <div className="flex px-4 py-2 lg:p-0 justify-between items-center border lg:border-none lg:bg-white">
                                                                    <p className=" lg:text-black text-sm">GST Total</p>
                                                                    <p className=" text-xs text-end lg:font-normal lg:text-black w-fit min-w-[164px] lg:px-4 py-2">Rs. {formatAmount(totalProposal - overallTotalExclGST)}</p>
                                                                </div>
                                                            </div>

                                                            {/* Total Quotation Value Amount Section */}
                                                            <div className="">
                                                                <hr className="hidden lg:block" />
                                                                <div className="flex px-4 py-2 lg:p-0 justify-between items-center border lg:border-none lg:bg-white">
                                                                    <p className=" lg:text-black text-sm">Total Quotation Value (inc. GST)</p>
                                                                    <p className="  text-xs text-end lg:font-normal lg:text-black w-fit min-w-[150px] lg:px-4 py-2">Rs. {formatAmount(totalProposal)}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </MobileSectionCard>
                                        )}
                                        {/* {currentStep === 3 && (
                                            <div>
                                                <div className="mb-8 lg:mb-[40px]">
                                                    {attachmentsData.length > 0 ? (
                                                        <>
                                                            <h3 className="text-sm font-semibold text-[#3B3B3B] lg:hidden">Supporting Documents</h3>
                                                            <p className="text-xs font-normal text-[#9D9D9D] pb-4 lg:hidden">Below are the supporting documents uploaded by the buyer. Please review them.</p>
                                                            <div className="lg:grid lg:grid-cols-4 lg:gap-4 space-y-4 lg:space-y-0 lg:mt-4">
                                                                {attachmentsData.map((doc, index) => (
                                                                    <div key={index} className="flex justify-between items-center">
                                                                        <div className="flex w-fit items-center justify-center border border-[#8E8E85] rounded-full py-2 px-2 space-x-2 text-[#434343]">
                                                                            <img src={`/assets/icons/${doc.Path}.png`} alt={doc.Path} className="h-5 w-5" />
                                                                            <a
                                                                                href={doc.Content}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                download={doc.Name}
                                                                                className="text-xs text-[#434343] truncate hover:underline"
                                                                            >
                                                                                {doc.Name}
                                                                            </a>
                                                                        </div>
                                                                        <img src="https://storagereponeevaydevcdn.blob.core.windows.net/business/download_solid.svg" alt="" className="lg:hidden" />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </>
                                                    ) :
                                                        <>
                                                            <div className="text-gray-400 italic">No Supporting Documents provided by Buyer, move to next step </div>
                                                        </>
                                                    }
                                                </div>
                                            </div>
                                        )} */}
                                        {(
                                            <>
                                                {/* Additional Information Section */}
                                                <MobileSectionCard
                                                    number="3"
                                                    title="Additional Information"
                                                    description="Confirm payment terms and add any remarks for the client."
                                                >

                                                    <div className="lg:flex justify-between items-center">

                                                        <div className="lg:hidden justify-between items-center rounded-xl border border-[#E7E7DD] bg-[#FCFCFA] p-4">
                                                            <p className="text-sm font-semibold text-[#303026]">
                                                                Do you agree to the payment terms?
                                                            </p>
                                                            <div className="grid grid-cols-2 gap-3 mt-3">
                                                                <label className={`inline-flex items-center rounded-lg border px-3 py-2 text-sm ${!isCounterOfferSelected(values.agreedToPaymentTerms) ? "border-[#1E1E08] bg-[#F2F2EA]" : "border-[#E0E0D6] bg-white"}`}>
                                                                    <input
                                                                        type="radio"
                                                                        name="labourMobileAgreedToPaymentTerms"
                                                                        checked={!isCounterOfferSelected(values.agreedToPaymentTerms)}
                                                                        onChange={() => {
                                                                            setFieldValue('agreedToPaymentTerms', true);
                                                                            setFieldValue('counterPaymentTerms', '');
                                                                            setIsCounterTerm(false);
                                                                        }}
                                                                        className="form-radio disabled:cursor-not-allowed"
                                                                        disabled={isReadOnly}
                                                                    />
                                                                    <span className="ml-2 ">Yes</span>
                                                                </label>
                                                                <label className={`inline-flex items-center rounded-lg border px-3 py-2 text-sm ${isCounterOfferSelected(values.agreedToPaymentTerms) ? "border-[#1E1E08] bg-[#F2F2EA]" : "border-[#E0E0D6] bg-white"}`}>
                                                                    <input
                                                                        type="radio"
                                                                        name="labourMobileAgreedToPaymentTerms"
                                                                        checked={isCounterOfferSelected(values.agreedToPaymentTerms)}
                                                                        onChange={() => {
                                                                            setFieldValue('agreedToPaymentTerms', false);
                                                                            setIsCounterTerm(true);
                                                                        }}
                                                                        className="form-radio disabled:cursor-not-allowed"
                                                                        disabled={isReadOnly}
                                                                    />
                                                                    <span className="ml-2">Counter Offer</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                        {isCounterOfferSelected(values.agreedToPaymentTerms) && <div className="mt-4">
                                                            <p className="font-semibold text-sm text-[#4F4F42]">Counter Payment Terms</p>
                                                            <Field
                                                                as="textarea"
                                                                name="counterPaymentTerms"
                                                                className={`lg:w-[390px] w-full text-sm h-[144px] my-2 rounded-xl border border-[#DCDCD0] px-3 py-2 placeholder:italic ${isReadOnly ? "bg-gray-100" : "bg-white"}`}
                                                                placeholder="Please write your counter payment terms Rich Text "
                                                                disabled={isReadOnly}
                                                            />
                                                        </div>}
                                                    </div>
                                                    <div className=" mt-4">
                                                        <p className="text-[#434343] mb-2 lg:mb-0">Any Remark for Client?</p>
                                                        <Field
                                                            as="textarea"
                                                            name="notes"
                                                            placeholder="Add Notes (Optional) "
                                                            className={`rounded-xl border-[#CDCDB8] w-full focus:ring-0 px-3 py-2 text-sm placeholder:text-sm lg:ml-5 lg:w-full h-[125px] placeholder:italic placeholder-[#8C8C8C] ${isReadOnly ? "bg-gray-100" : "bg-white"}`}
                                                            disabled={isReadOnly}
                                                        />
                                                    </div>
                                                </MobileSectionCard>

                                                {/* Upload Supporting Documents */}
                                                <MobileSectionCard
                                                    number="4"
                                                    title="Upload Supporting Documents"
                                                    description="Attach proposal files or supporting documents if required."
                                                >

                                                    {attachmentsSkeleton.length > 0 && (
                                                        <p className="mb-3 rounded-lg bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-800">
                                                            Your file is being scanned for upload, be patient and continue to fill
                                                            the rest of the form.
                                                        </p>
                                                    )}

                                                    <div className="mb-3">
                                                        {/* Display the selected file names */}
                                                        <ul className="flex flex-wrap gap-2">
                                                            {selectedFilesBase.map((file, index) => (
                                                                <a
                                                                    href={file.Content}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="border border-[#D9D9CF] bg-[#FCFCFA] px-3 py-1 rounded-full flex items-center"
                                                                    key={file.Name}
                                                                >
                                                                    <span className="text-sm overflow-hidden whitespace-nowrap w-36 truncate">
                                                                        {file.Name}
                                                                    </span>
                                                                    {!isReadOnly && (
                                                                        <span
                                                                            className="ml-1 cursor-pointer"
                                                                            onClick={(event) => handleDeleteSelectedFile(file.Name, event)}
                                                                        >
                                                                            <svg
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                width="18"
                                                                                height="18"
                                                                                viewBox="0 0 48 48"
                                                                            >
                                                                                <circle cx="24" cy="24" r="22" fill="gray" />
                                                                                <line
                                                                                    x1="15"
                                                                                    y1="15"
                                                                                    x2="33"
                                                                                    y2="33"
                                                                                    stroke="white"
                                                                                    strokeWidth="2"
                                                                                />
                                                                                <line
                                                                                    x1="33"
                                                                                    y1="15"
                                                                                    x2="15"
                                                                                    y2="33"
                                                                                    stroke="white"
                                                                                    strokeWidth="2"
                                                                                />
                                                                            </svg>
                                                                        </span>
                                                                    )}
                                                                </a>
                                                            ))}

                                                            {/* Skeleton loaders for files being processed */}
                                                            {attachmentsSkeleton.map((_, index) => (
                                                                <Skeleton
                                                                    key={index}
                                                                    variant="rounded"
                                                                    sx={{ borderRadius: "20px", margin: "4px" }}
                                                                    width={150}
                                                                    height={32}
                                                                />
                                                            ))}
                                                        </ul>
                                                        {isReadOnly && selectedFilesBase.length === 0 && attachmentsSkeleton.length === 0 && (
                                                            <p className="rounded-lg border border-dashed border-[#DCDCD0] bg-[#FCFCFA] px-4 py-3 text-sm text-[#7A7A68]">
                                                                No supporting documents uploaded.
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        {attachmentsUploadsErrors && (
                                                            <div className="mt-3">
                                                                <p className="rounded-lg bg-red-50 px-3 py-2 text-red-600 font-semibold text-sm">
                                                                    {attachmentsUploadsErrors}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Error handling for file size */}
                                                    <div
                                                        className={`fixed bottom-0 left-2  p-4 ${openFilesSizeError ? "block" : "hidden"
                                                            }`}
                                                    >
                                                        <div className="bg-orange-100 w-[calc(100vw-1rem)] max-w-[440px] flex items-center justify-between px-4 py-1 text-yellow-900 rounded-lg shadow-md p-4">
                                                            <p>Total size of attachments is not more than 10 MB</p>
                                                            <button
                                                                onClick={handleCloseFileSizeError}
                                                                className="px-4 py-2 focus:outline-none"
                                                            >
                                                                <svg
                                                                    className="h-6 w-6"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    stroke="currentColor"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M6 18L18 6M6 6l12 12"
                                                                    ></path>
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>


                                                    {/* drag and drop file */}
                                                    {!isReadOnly && <div className="flex items-center justify-center h-[175px] rounded-2xl bg-[#F6FAFF] border-2 border-dashed border-[#8EA8FF] relative">
                                                        <div className="lg:hidden flex flex-col items-center justify-center">
                                                            <img src="https://storagereponeevaydevcdn.blob.core.windows.net/business/create_rfq_upload.svg" alt="" className="mx-auto" />
                                                            <p className="text-center mt-4 text-sm font-semibold text-[#303026]">Upload your files here</p>
                                                            <p className="mt-1 text-center text-xs text-[#7A7A68]">PDF, image, Excel, CSV or DWG files</p>
                                                            <label htmlFor="file" className="mt-2 text-[#1D28FA] underline text-center cursor-pointer">
                                                                Browse
                                                            </label>
                                                        </div>
                                                        <input
                                                            type="file"
                                                            accept=".pdf,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.csv,.dwg" // Specify allowed file types
                                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                            onClick={handleFileUploadClick}
                                                            onChange={handleFileUpload}
                                                            multiple // Allow multiple files to be selected
                                                        />
                                                    </div>}
                                                </MobileSectionCard>
                                            </>
                                        )}

                                        {/* {currentStep === 4 &&
                                            (<div className="text-gray-400 italic">No Supporting Documents provided by Buyer, move to next step </div>)
                                        } */}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* desktop content */}
                        <div className="hidden lg:block quotation-desktop-surface">
                            <QuotationDesktopStyles />
                            <div className="w-full shadow-md" style={{ fontFamily: "goldman_sans" }}>
                                {false && shouldShowVendorRank(rfqData) && (
                                    <div className="flex items-center justify-end px-8 pt-4">
                                        <div className="flex items-center space-x-2">
                                            <span class="relative flex items-center justify-center h-4 w-4 ">
                                                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-500 opacity-40"></span>
                                                <span class="relative inline-flex rounded-full h-2 w-2 bg-lime-500"></span>
                                            </span>
                                            <h3 className="text-sm lg:mt-1 text-center text-[#737373]">
                                                Quotation Rank:{" "}
                                                <span className="font-bold text-black">
                                                    {currentRank === "L1"
                                                        ? "L2"
                                                        : currentRank === undefined || currentRank === null
                                                            ? "Quotation not submitted"
                                                            : currentRank}
                                                </span>
                                            </h3>
                                            <span className="bg-[#cac9c9] text-white text-xs h-[12px] w-[12px] pt-1 flex justify-center items-center rounded-full relative group hover:cursor-pointer">
                                                i
                                                <span className="bg-white px-2 absolute -bottom-5 hidden w-max  text-black text-xs  group-hover:block">
                                                    {currentRank === undefined || currentRank === null
                                                        ? "Quotation rank is available after quotation submission"
                                                        : "Based on your submitted quotation"}
                                                </span>
                                            </span>
                                            {/* <p className="text-[#9F9F9F] lg:mt-1 text-xs italic hidden lg:block">Your rank will update as soon as the buyer receives atleast 3 ratings</p> */}
                                        </div>
                                    </div>
                                )}


                                {/* rfq information */}
                                <RfqInformation
                                    rfqData={rfqData}
                                    togglePopup={togglePopup}
                                    isPopupOpen={isPopupOpen}
                                    isAccordion
                                />


                                <div className="px-4 lg:px-8 lg:space-y-6">
                                    {/*desktop Labour */}
                                    <div className="rounded-2xl border border-[#E7E7DD] bg-white p-6 shadow-sm">
                                        <div className="flex items-center mb-2">
                                            <h3 className="whitespace-nowrap text-[#434343] hidden lg:block uppercase text-sm font-semibold">Labour</h3>
                                            <hr className="flex-grow border-t hidden lg:block border-gray-300 mx-4" />
                                            <div className="flex flex-col items-end space-y-2">
                                                <div className="flex space-x-4">
                                                    <button
                                                        type="button"
                                                        disabled={excelLoading}
                                                        onClick={() =>
                                                            handleDownloadLabourExcel(
                                                                rfqData,
                                                                values.labourItems,
                                                                setExcelLoading,
                                                                setLoadingMessage,
                                                                setExcelError
                                                            )
                                                        }

                                                        // onClick={() => handleDownloadExcelForContractor(isEditing, rfqData, contractorDetails, setExcelLoading, setLoadingMessage)}
                                                        className="bg-blue-500 text-white px-3 py-1 text-xs"
                                                    >
                                                        Download Requirement
                                                    </button>
                                                    <label className="border border-gray-800 px-3 py-1 cursor-pointer text-xs">
                                                        Upload Rates
                                                        <input
                                                            type="file"
                                                            accept=".xlsx"
                                                            disabled={excelLoading}
                                                            onChange={(e) =>
                                                                handleUploadLabourExcel(
                                                                    e,
                                                                    values,
                                                                    setFieldValue,
                                                                    rfqData,
                                                                    setExcelLoading,
                                                                    setLoadingMessage,
                                                                    enqueueSnackbar,
                                                                    calculateOverallTotal,
                                                                    calculateOverallTotalWithoutGST,
                                                                    setTotalProposal,
                                                                    miscellaneousCosts,
                                                                    setExcelError
                                                                )
                                                            }
                                                            // onChange={(event) => handleUploadMaterialExcel(event,
                                                            //     setFieldValue,
                                                            //     values,
                                                            //     rfqData,
                                                            //     setExcelLoading,
                                                            //     setLoadingMessage,
                                                            //     enqueueSnackbar,
                                                            //     calculateOverallTotals,
                                                            //     calculateMaterialTotals,
                                                            //     calculateScopeTotals)}
                                                            className="hidden"
                                                        />
                                                    </label>
                                                </div>
                                                <p className="text-[10px] italic text-gray-500">Note: Download requirement for uploading rates</p>
                                            </div>
                                        </div>
                                        {/* <p className="text-xs font-medium hidden lg:block text-[#434343] mt-2 mb-4">Your bid value is the sum of these line items</p> */}

                                        {/* input fields */}

                                        {/* Line items Section */}
                                        {/* <p className="text-[32px] font-bold text-[rgb(67,67,67)] hidden lg:block">Line Items</p> */}
                                        <div className="mb-5 hidden lg:grid lg:grid-cols-16 items-center lg:gap-4 text-[#777777] text-xs font-normal  border-y py-2">
                                            <label className="block font-medium col-span-1 text-end">Sr#</label>
                                            <label className="block font-medium col-span-6">Work description</label>
                                            <label className="block font-medium col-span-1">Qty/Unit</label>
                                            {/* <label className="block text-center font-medium col-span-1 ">Unit</label> */}
                                            <label className="block font-medium text-center col-span-2">Rate</label>
                                            <label className="block font-medium col-span-1 text-center">GST (%)</label>
                                            {/* <label className="block font-medium col-span-2 text-center">WORK START DATE</label> */}
                                            <label className="block text-center font-medium col-span-1">Total cost</label>
                                            <label className="block text-center font-medium col-span-3 ">Remarks</label>
                                            <label className="block text-center font-medium col-span-1">Attach.</label>
                                        </div>


                                        <div className="space-y-6 mb-8 text-xs">
                                            {rfqData?.labourDetails?.map((detail, index) => (
                                                <div key={index} className="desktop-labour-item">
                                                    <div className="lg:grid lg:grid-cols-16 gap-2 hidden text-[#373737]">
                                                        <div className="col-span-1 flex justify-evenly text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={sectionCheckboxes[index] || false}
                                                                onChange={(e) => handleLabourCheckboxChange(index, e.target.checked)}
                                                                className="h-4 w-4 text-blue-600 rounded cursor-pointer"
                                                                disabled={isReadOnly}
                                                            />
                                                            <p className="text-xs">{index + 1}</p>
                                                        </div>
                                                        {/* Work description */}
                                                        {detail?.skill?.length > 0 && (
                                                            <div className="col-span-6">
                                                                <p className="font-light whitespace-pre-line">
                                                                    {ReactHtmlParser(
                                                                        expandedStates[index]
                                                                            ? detail?.skill
                                                                            : detail?.skill?.substring(0, 220) +
                                                                            (detail?.skill?.length > 220
                                                                                ? "..."
                                                                                : "")
                                                                    )}
                                                                </p>
                                                                {detail?.skill?.length > 220 && (
                                                                    <button
                                                                        type="button"
                                                                        className="text-[#8E8E85] text-xs underline italic"
                                                                        onClick={() => toggleExpanded(index)}
                                                                    >
                                                                        {expandedStates[index] ? "Read Less" : "Read More"}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}

                                                        <p className="border-none text-xs break-words col-span-1 max-h-[120px] overflow-y-auto placeholder-[#373737] ">
                                                            {detail?.quantity} <span className="font-semibold">{getFormattedUnit(detail?.unit)}</span>
                                                        </p>

                                                        {/* Labour rate input */}
                                                        <div className="col-span-2 justify-self-center">
                                                            <Field
                                                                name={`labourItems[${index}].labourRate`}
                                                                type="text"
                                                                className={`h-[30px]  focus:ring-0 text-xs border-[#DCDCD0] text-right self-end border px-2 py-1 ${isReadOnly && "bg-gray-100"}`}
                                                                onChange={e => handleLabourRateChange(index, e.target.value, setFieldValue, values.labourItems)}
                                                                disabled={isReadOnly}
                                                            />
                                                            <ErrorMessage name={`labourItems[${index}].labourRate`} component="div" className="text-red-500 text-xs absolute" />
                                                        </div>

                                                        {/* GST Dropdown */}
                                                        <div className="col-span-1 flex justify-center h-fit mx-auto lg:w-2/3">
                                                            <CustomGstDropdown
                                                                options={[0, 5, 12, 18, 28]}
                                                                value={values.labourItems[index]?.gst || ''}
                                                                onChange={value => handleGstChange(index, value, setFieldValue, values.labourItems)}
                                                                className="w-full justify-between h-[30px]"
                                                                disabled={isReadOnly}
                                                            />
                                                        </div>

                                                        {/* Total amount */}
                                                        <div className="col-span-1 justify-self-end">
                                                            <div className="p-2 w-[120px] lg:w-auto h-[44px] mt-2 col-span-1 text-right break-words placeholder-[#373737]">
                                                                {formatAmount(values.labourItems[index].totalExclGst)}
                                                            </div>

                                                        </div>


                                                        {/* User-entered remark */}
                                                        <div className="col-span-3 flex flex-col">
                                                            <Field
                                                                as="textarea"
                                                                name={`labourItems[${index}].remarks`}
                                                                rows={1}
                                                                className="lg:w-full min-h-[40px] max-h-[200px] overflow-y-auto focus:ring-0 text-xs border-[#DCDCD0] border px-2 py-1 resize-none"
                                                                onChange={(e) => {
                                                                    handleRemarkChange(index, e.target.value, setFieldValue);

                                                                    // Adjust the textarea height
                                                                    e.target.style.height = "auto"; // Reset height first
                                                                    e.target.style.height = `${e.target.scrollHeight}px`; // Set height based on content
                                                                }}
                                                                disabled={isReadOnly}
                                                            />
                                                            <ErrorMessage name={`labourItems[${index}].remark`} component="div" className="text-red-500 text-xs" />
                                                        </div>

                                                        <div className="relative text-center col-span-1 mt-2">
                                                            <AttachFileIcon
                                                                className="hover:cursor-pointer"
                                                                sx={{
                                                                    width: "0.7em", ml: "2px",
                                                                    transform: 'rotate(45deg)',
                                                                    transition: 'transform 0.3s ease'
                                                                }}
                                                                onClick={() => {
                                                                    setSelectedLabourIndex(index); // Set the labor index
                                                                    setOpenUploadFile(true); // Open the file upload popup
                                                                }}
                                                            />

                                                            {values.labourItems[index]?.attachments?.length > 0 && (
                                                                <span
                                                                    className="absolute top-0 right-8 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
                                                                    style={{ transform: 'translate(50%, -50%)' }}
                                                                >
                                                                    {values.labourItems[index].attachments.length}
                                                                </span>
                                                            )}
                                                        </div>

                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                    </div>

                                    <div className="text-xs">
                                        {(() => {
                                            const chargeLabels = {
                                                // transport: "transport",
                                                mathadeCharges: "mathade",
                                                // loading: "loading",
                                                // unLoading: "unloading",
                                                miscCharges: "miscellaneous",
                                            };

                                            const absentCharges = Object.entries(rfqData?.otherCharges || {})
                                                .filter(([_, value]) => value === false)
                                                .map(([key]) => chargeLabels[key])
                                                .filter(Boolean);

                                            if (absentCharges.length === 0) return null;

                                            const formattedCharges =
                                                absentCharges.length === 1
                                                    ? absentCharges[0]
                                                    : absentCharges.slice(0, -1).join(", ") + " and " + absentCharges.slice(-1);

                                            const isPlural = absentCharges.length > 1;

                                            return (
                                                <p>
                                                    <b> IMPORTANT NOTE:</b> Please note that if {formattedCharges} charges are applicable,
                                                    {isPlural ? " they must" : " it must"} be included in the rate and applicable charges will
                                                    be taken care by vendor unless mentioned in the terms otherwise.
                                                </p>
                                            );
                                        })()}
                                    </div>

                                    {/* miscellaneous */}
                                    <div className="additional-cost-card p-8 bg-white shadow-md">
                                        {hasVisibleMiscellaneousItems && (
                                            <div className="pb-6">
                                                <div className="hidden lg:block">
                                                    <h3 className="text-sm font-semibold text-[#434343] hidden lg:block mb-3">Additional Cost Components</h3>

                                                    <div className="text-xs bg-white border py-4 shadow-md lg:border-none lg:mx-0 lg:shadow-none lg:py-0">
                                                        <div className="hidden lg:grid lg:grid-cols-12 items-center gap-2 text-[#777777] font-normal border-y py-2">
                                                            <label className="block font-medium col-span-2">Charges</label>
                                                            <label className="block font-medium col-span-1">Rate</label>
                                                            <label className="block font-medium text-center col-span-1">GST (%)</label>
                                                            <label className="block font-medium col-span-6">Remarks</label>
                                                            {/* <label className="font-medium text-start col-span-1 flex justify-center items-center relative">
                                                                MRP
                                                                <span className="bg-[#cac9c9] text-white text-xs h-[15px] w-[15px] flex justify-center items-center mb-1 ml-1 pt-1 rounded-full lowercase relative group hover:cursor-default">
                                                                    i
                                                                    <span className="absolute bottom-[130%] text-xs left-1/2 w-[200px] transform -translate-x-1/2 px-2 py-1 text-[#434343] text-left bg-white border border-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                                                        Enter the cost per unit for the selected construction material. Ensure accuracy as this value impacts overall project budgeting and pricing.
                                                                    </span>
                                                                </span>
                                                            </label> */}
                                                            <label className="block font-medium text-right col-span-2">Total Cost</label>
                                                        </div>

                                                        {/* Render visible miscellaneous items */}
                                                        {visibleMiscellaneousItems.map((item) => (
                                                            <div key={item.name} className="lg:grid lg:grid-cols-12 gap-1 mb-8 lg:mb-0 space-y-3 lg:space-y-2 items-center">
                                                                <label className="block lg:mb-2 col-span-2 font-semibold lg:font-medium ">
                                                                    {item.label}
                                                                </label>

                                                                <div className="col-span-1">
                                                                    <Field
                                                                        name={`miscellaneousCosts.${item.name}.mrp`}
                                                                        type="text"
                                                                        className={`w-fit hidden text-right focus:ring-0 lg:block lg:w-full h-[30px] text-xs placeholder-[#373737] border border-gray-300 ${isReadOnly && "bg-gray-100"}`}
                                                                        placeholder="Rs."
                                                                        onChange={(e) => handleMiscellaneousChange(item.name, e.target.value, setFieldValue, values, 'mrp')}
                                                                        disabled={isReadOnly}
                                                                    />
                                                                    <ErrorMessage
                                                                        name={`miscellaneousCosts.${item.name}.mrp`}
                                                                        component="div"
                                                                        className="text-red-500 text-xs mt-1"
                                                                    />
                                                                </div>

                                                                {/* GST Dropdown */}
                                                                <div className="col-span-1 lg:flex hidden justify-center h-fit lg:w-full w-[262px]">
                                                                    <CustomGstDropdown
                                                                        options={[0, 5, 12, 18, 28]}
                                                                        value={values.miscellaneousCosts[item.name]?.gst}
                                                                        onChange={(value) => handleMiscGstChange(item.name, value, setFieldValue, values)}
                                                                        className="lg:w-[64px] w-[262px] justify-between h-[30px]"
                                                                        disabled={isReadOnly}
                                                                    />
                                                                    <ErrorMessage
                                                                        name={`miscellaneousCosts.${item.name}.gst`}
                                                                        component="div"
                                                                        className="text-red-500 text-xs mt-1"
                                                                    />
                                                                </div>

                                                                {/* Remark */}
                                                                <div className="col-span-6 lg:flex hidden justify-center h-fit lg:w-full w-[262px]">
                                                                    <ReactQuill
                                                                        value={values.miscellaneousCosts[item.name]?.remark || ''}
                                                                        name={`miscellaneousCosts.${item.name}.remark`}
                                                                        onChange={(value) =>
                                                                            handleMiscellaneousChange(item.name, value, setFieldValue, values, "remark")
                                                                        }
                                                                        disabled={isReadOnly}
                                                                        placeholder="Please add notes, if any"
                                                                        theme="snow"
                                                                        className="min-h-[20px] max-h-[100px] text-xs word-break p-0 py-0 px-0 focus:outline-none focus:ring-0 bg-white border-[#DCDCD0] border w-full overflow-y-auto remove-quill-padding"
                                                                        modules={{ toolbar: false }}
                                                                    />
                                                                    <ErrorMessage
                                                                        name={`miscellaneousCosts.${item.name}.remark`}
                                                                        component="div"
                                                                        className="text-red-500 text-xs"
                                                                    />
                                                                </div>
                                                                {/* Total Cost */}
                                                                <p className="w-full col-span-2 text-right text-[#004491] font-bold lg:font-normal lg:text-black">
                                                                    Rs. {formatAmount(values.miscellaneousCosts[item.name]?.totalCost)}
                                                                </p>



                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="">
                                            <hr className="hidden lg:block" />
                                            <div className="flex py-5 lg:p-0 justify-between items-center border lg:border-none border-t-[#007366] bg-[#E0F0EE] lg:bg-white">
                                                <p className=" text-[#007366] lg:text-black text-sm">Total Basic Amount</p>
                                                <p className=" text-xs text-end lg:font-normal lg:text-black w-fit min-w-[164px] lg:pl-4 lg:pr-0 py-2">Rs. {formatAmount(overallTotalExclGST)}</p>
                                            </div>

                                            {/* GST Amount Section */}
                                            <div className="">
                                                <hr className="hidden lg:block" />
                                                <div className="flex px-4 py-5 lg:p-0 justify-between items-center border lg:border-none border-t-[#007366] bg-[#E0F0EE] lg:bg-white">
                                                    <p className="text-[#007366] lg:text-black text-sm">GST Total</p>
                                                    <p className="text-[#007366] text-xs text-end lg:font-normal lg:text-black w-fit min-w-[164px] lg:pl-4 lg:pr-0 py-2">Rs. {formatAmount(totalProposal - overallTotalExclGST)}</p>
                                                </div>
                                            </div>

                                            {/* Total Quotation Value Amount Section */}
                                            <div className="">
                                                <hr className="hidden lg:block" />
                                                <div className="flex px-4 py-5 lg:p-0 justify-between items-center border lg:border-none border-t-[#007366] bg-[#E0F0EE] lg:bg-white">
                                                    <p className="text-[#007366] lg:text-black text-sm">Total Quotation Value (inc. GST)</p>
                                                    <p className="bg-[#E0F0EE] text-[#007366] text-xs text-end lg:font-normal lg:text-black w-fit min-w-[164px] lg:pl-4 lg:pr-1 py-2">Rs. {formatAmount(totalProposal)}</p>
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    {/* Supporting Documents Section */}
                                    {/* {attachmentsData.length > 0 && (
                                        <div className="mb-8 hidden lg:block bg-white p-8 shadow-md">
                                            <>
                                                <h3 className="text-sm font-semibold text-[#434343] hidden lg:block">Supporting Documents</h3>
                                                <p className="text-xs font-normal text-[#9D9D9D] "> <span className="font-bold text-gray-500">Note: </span>  Below are the supporting documents uploaded by the buyer. Please review them.</p>
                                                <div className="lg:grid lg:grid-cols-4 lg:gap-4 space-y-4 lg:space-y-0 lg:mt-4 mx-auto">
                                                    {attachmentsData.map((doc, index) => (
                                                        <div key={index} className="flex justify-between items-center mx-2">
                                                            <div className="px-2  flex items-center justify-center border border-[#8E8E85] rounded-full py-1 space-x-2 text-[#434343]">
                                                                <img src={`/assets/icons/${doc.Path}.png`} alt={doc.Path} className="h-5 w-5" />
                                                                <a
                                                                    href={doc.Content}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    download={doc.Name}
                                                                    className="text-xs text-[#434343] hover:underline"
                                                                >
                                                                    {doc.Name}
                                                                </a>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        </div>
                                    )} */}

                                    {/* Additional Information Section */}
                                    <div className="mb-6 bg-white p-8 shadow-md">
                                        <h3 className="text-sm font-semibold text-[#434343] hidden lg:block">Additional Information</h3>

                                        <div className="flex items-center space-x-8">
                                            <p className="mb-2 text-sm ">
                                                Do you agree to the payment terms ?
                                            </p>
                                            <div className="flex space-x-4 mb-4">
                                                <label className="inline-flex items-center">
                                                    <Field
                                                        type="radio"
                                                        name="agreedToPaymentTerms"
                                                        value="true"
                                                        checked={!isCounterOfferSelected(values.agreedToPaymentTerms)} // Controlled check status
                                                        onChange={() => {
                                                            setFieldValue('agreedToPaymentTerms', true);
                                                            setFieldValue('counterPaymentTerms', '');
                                                            setIsCounterTerm(false); // Update counter term
                                                        }}
                                                        disabled={isReadOnly}
                                                        className="form-radio"
                                                    />
                                                    <span className="ml-2 text-sm">Yes</span>
                                                </label>

                                                <label className="inline-flex items-center">
                                                    <Field
                                                        type="radio"
                                                        name="agreedToPaymentTerms"
                                                        value="false"
                                                        checked={isCounterOfferSelected(values.agreedToPaymentTerms)} // Controlled check status
                                                        onChange={() => {
                                                            setFieldValue('agreedToPaymentTerms', false);
                                                            setIsCounterTerm(true); // Update counter term
                                                        }}
                                                        disabled={isReadOnly}
                                                        className="form-radio"
                                                    />
                                                    <span className="ml-2 text-sm">Counter Offer</span>
                                                </label>
                                            </div>
                                            <ErrorMessage name="agreedToPaymentTerms" component="div" className="text-red-500 text-xs " />
                                        </div>
                                        <div className=" mb-6">

                                            {isCounterOfferSelected(values.agreedToPaymentTerms) && <div className=" mx-4 lg:mx-0">
                                                <p className="font-medium text-sm pb-1 text-[#4F4F42]">Counter Payment Terms</p>
                                                <Field
                                                    as="textarea"
                                                    name="counterPaymentTerms"
                                                    className={`w-full h-[120px] text-xs placeholder:text-xs border border-gray-300 placeholder:italic 
                                                    ${values.agreedToPaymentTerms === true ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                                                    placeholder="Please write your counter payment terms"
                                                    disabled={values.agreedToPaymentTerms === true || isReadOnly}
                                                />
                                            </div>}
                                        </div>
                                        <div className="">
                                            <p className="text-[#434343] text-sm mb-2 lg:mb-0">Any Remark for Client?</p>
                                            <Field
                                                as="textarea"
                                                name="notes"
                                                placeholder="Add Notes (Optional) "
                                                className={`border-[#CDCDB8] mt-1 w-full focus:ring-0 text-xs placeholder:text-xs lg:w-full h-[120px] placeholder:italic placeholder-[#8C8C8C] ${isReadOnly && "bg-gray-100"}`}
                                            />
                                        </div>
                                    </div>

                                    {/* Upload Supporting Documents */}
                                    <div className="pb-8">
                                        <div className="pb-8 mx-4 lg:mx-0 shadow-md bg-white p-8">
                                            <h3 className="whitespace-nowrap text-[#434343] text-sm font-bold pb-4 lg:pb-0 lg:mb-[15px]">Supporting Documents</h3>
                                            {isReadOnly && selectedFilesBase.length === 0 && attachmentsSkeleton.length === 0 && (
                                                            <p className="rounded-lg border border-[#DCDCD0] bg-[#FCFCFA] px-4 py-3 mb-2 text-sm text-[#7A7A68]">
                                                                No supporting documents uploaded.
                                                            </p>
                                                        )}
                                            {attachmentsSkeleton.length > 0 && (
                                                <p className="text-xs pl-3">
                                                    Your file is being scanned for upload, be patient and continue to fill
                                                    the rest of the form.
                                                </p>
                                            )}

                                            <div className="">
                                                <ul style={{ display: "flex", flexWrap: "wrap" }}>
                                                    {selectedFilesBase.map((file, index) => (
                                                        <a
                                                            href={file.Content}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="border border-gray-300 px-3 py-1 rounded-full flex mb-4 ml-10"
                                                            key={file.Name}
                                                        >
                                                            <span className="text-sm overflow-hidden whitespace-nowrap w-36 truncate">
                                                                {file.Name}
                                                            </span>
                                                            {!isReadOnly && (
                                                                <span
                                                                    className="ml-1 cursor-pointer"
                                                                    onClick={(event) => handleDeleteSelectedFile(file.Name, event)}
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="18"
                                                                        height="18"
                                                                        viewBox="0 0 48 48"
                                                                    >
                                                                        <circle cx="24" cy="24" r="22" fill="gray" />
                                                                        <line
                                                                            x1="15"
                                                                            y1="15"
                                                                            x2="33"
                                                                            y2="33"
                                                                            stroke="white"
                                                                            strokeWidth="2"
                                                                        />
                                                                        <line
                                                                            x1="33"
                                                                            y1="15"
                                                                            x2="15"
                                                                            y2="33"
                                                                            stroke="white"
                                                                            strokeWidth="2"
                                                                        />
                                                                    </svg>
                                                                </span>
                                                            )}
                                                        </a>
                                                    ))}

                                                    {/* Skeleton loaders for files being processed */}
                                                    {attachmentsSkeleton.map((_, index) => (
                                                        <Skeleton
                                                            key={index}
                                                            variant="rounded"
                                                            sx={{ borderRadius: "20px", margin: "4px" }}
                                                            width={150}
                                                            height={32}
                                                        />
                                                    ))}
                                                </ul>
                                            </div>

                                            <div>
                                                {attachmentsUploadsErrors && (
                                                    <div className="mt-3">
                                                        <p className="text-red-500 font-semibold text-base mt-14">
                                                            {attachmentsUploadsErrors}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Error handling for file size */}
                                            <div
                                                className={`fixed bottom-0 left-2  p-4 ${openFilesSizeError ? "block" : "hidden"
                                                    }`}
                                            >
                                                <div className="bg-orange-100 w-[440px] flex items-center justify-between  px-4 py-1 text-yellow-900 rounded-lg shadow-md p-4">
                                                    <p>Total size of attachments is not more than 10 MB</p>
                                                    <button
                                                        onClick={handleCloseFileSizeError}
                                                        className="px-4 py-2 focus:outline-none"
                                                    >
                                                        <svg
                                                            className="h-6 w-6"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth="2"
                                                                d="M6 18L18 6M6 6l12 12"
                                                            ></path>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>


                                            {/* drag and drop file */}
                                            {!isReadOnly && <div className="lg:mr-0 flex items-center justify-center h-[175px] lg:h-[180px] bg-[#F1F7FF] lg:bg-white border-2 border-dashed border-[#004491] lg:border-[#7D7D7D] relative">
                                                <input
                                                    type="file"
                                                    accept=".pdf,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.csv,.dwg" // Specify allowed file types
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                    onClick={handleFileUploadClick}
                                                    onChange={handleFileUpload}
                                                    multiple // Allow multiple files to be selected
                                                />
                                                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 pointer-events-none">
                                                    <div className="relative mx-auto hidden lg:block">
                                                        {/* Overlapping rectangles */}
                                                        <div className="absolute top-10 -left-8 h-[80px] w-[65px] bg-white border border-[#878787]"></div>
                                                        <div className="absolute top-2 left-0 h-[80px] w-[65px] bg-white border border-[#878787]"></div>
                                                    </div>
                                                    {/* Centered text */}
                                                    <p className="mt-32 text-sm text-center hidden lg:block">Drag & Drop or Click to upload your proposal instead</p>
                                                </div>
                                            </div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* action buttons */}
                        <div className="sticky bottom-0 z-20">
                            {/* Action Buttons for mobile */}
                            {!isReadOnly && <div
                                style={{ boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                className="lg:hidden grid grid-cols-2 gap-3 bg-white p-4"
                            >
                                <button
                                    type="button"
                                    className="rounded-xl border border-[#1E1E08] px-4 py-3 text-sm font-semibold text-[#434343]"
                                    onClick={handleCloseForm}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="rounded-xl bg-[#1E1E08] px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
                                    disabled={loading}
                                    onClick={() => handleSubmitWithValidation(validateForm, setTouched, values, submitForm)}
                                >
                                    {loading ? "Submitting..." : isEdit ? "Update Quotation" : "Submit"}
                                </button>
                            </div>}
                            {/* Action Buttons for desktop*/}
                            {!isReadOnly && <div style={{ boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)' }} className="bg-white hidden space-x-3 lg:flex justify-end items-center py-2 lg:px-8 px-4 ">
                                <button type="button" className=" w-[96px] border border-[#1E1E08] px-3 py-1.5 text-xs text-[#434343] font-semibold" onClick={handleCloseForm}>
                                    Cancel
                                </button>
                                <button type="button" disabled={loading} onClick={() => handleSubmitWithValidation(validateForm, setTouched, values, submitForm)} className="bg-black border border-[#1E1E08] text-white px-4 py-1.5 text-xs min-w-[128px] flex justify-center items-center">
                                    {loading ? "Submitting..." : isEdit ? "Update Quotation" : "Submit Quotation"}
                                </button>
                            </div>}
                        </div>


                        <LoadingModal
                            isOpen={excelLoading || excelError !== null}
                            onClose={() => setExcelError(null)}
                        >
                            {excelLoading ? (
                                <div className="flex flex-col items-center justify-center">
                                    <div className="animate-spin rounded-full border-4 border-t-4 border-orange-500 border-t-gray-200 h-8 w-8 mb-4"></div>
                                    <p className="text-xl font-medium text-gray-600 italic">
                                        {loadingMessage}
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <p className="text-red-500 font-medium">
                                        {excelError || "Operation completed successfully!"}
                                    </p>
                                </div>
                            )}
                        </LoadingModal>
                        <StaticPopup
                            openModal={openUploadFile}
                            handleClose={() => setOpenUploadFile(false)}
                            size={"sm"}
                            width={"xl"}
                            bodyStyle="pb-0 pt-0"
                            className="px-0"
                            header={"Upload Supporting Documents"}
                        >
                            <FileUpload
                                handleFileUploadClick={handleLabourFileUploadClick} // Reuse the click handler
                                handleFileUpload={(event) => handleLabourFileUpload(event, selectedLabourIndex, setFieldValue, values)}
                                handleDeleteSelectedFile={(name, event) => handleDeleteLabourFile(name, event, setFieldValue, selectedLabourIndex, values)}
                                selectedFilesBase={values.labourItems[selectedLabourIndex]?.attachments || []}
                                attachmentsSkeleton={labourFilesSkeleton}
                                attachmentsUploadsErrors={labourUploadsErrors}
                                openFilesSizeError={openLabourFileSizeError}
                                handleCloseFileSizeError={handleCloseLabourFileSizeError} // Reuse the file size error handler
                                selectedMaterialIndex={selectedLabourIndex} // Keep this for file indexing
                                isReadOnly={isReadOnly}
                            />
                        </StaticPopup>

                        </div>
                    </Form>
                )}
            </Formik>
            <QuotationUpdateReasonModal
                open={showQuotationUpdateReasonModal}
                loading={loading}
                onCancel={() => {
                    setShowQuotationUpdateReasonModal(false);
                    setPendingQuotationPayload(null);
                }}
                onSubmit={handleQuotationUpdateReasonSubmit}
            />
            {/* Confirmation Popup */}
            {showConfirmPopup && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
                        <div className="flex justify-between mb-4">
                            <h3 className="text-base font-semibold text-gray-900">
                                {areAllSectionsChecked() ? 'Confirm Submission' : 'Review Required'}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowConfirmPopup(false)}
                                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                            >
                                <svg className="w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {areAllSectionsChecked() ? (
                            <p className="text-gray-700 mb-4 text-sm">
                                By submitting the Quotation, you agree that you have read all the work descriptions
                                carefully and will follow the work details.
                            </p>
                        ) : (
                            <p className="text-gray-700 mb-4 text-sm">
                                Please confirm that you have read and reviewed all work descriptions before submitting your quotation. All work descriptions must be checked to proceed.
                            </p>
                        )}

                        <div className="flex items-center my-3 p-3 bg-yellow-50 rounded">
                            <svg className="w-5 h-5 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-yellow-700">
                                {Object.values(sectionCheckboxes).filter(Boolean).length} of {Object.keys(sectionCheckboxes).length} work descriptions reviewed
                            </span>
                        </div>

                        <div className="flex justify-end space-x-4">
                            <button
                                type="button"
                                onClick={() => setShowConfirmPopup(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-sm"
                            >
                                {areAllSectionsChecked() ? 'Cancel' : 'Back'}
                            </button>

                            {/* Show Submit button only when all checkboxes are checked */}
                            {areAllSectionsChecked() && (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        if (!submissionValues) {
                                            console.error("No submission values available");
                                            return;
                                        }

                                        // Close popup first
                                        setShowConfirmPopup(false);

                                        // Prepare payload for submission
                                        const payload = {
                                            ...submissionValues,
                                            selectedFilesBase,
                                            otherCharges: submissionValues.miscellaneousCosts
                                        };
                                        if (isRfqCreatorUpdatingSubmittedQuotation) {
                                            setPendingQuotationPayload(payload);
                                            setShowQuotationUpdateReasonModal(true);
                                            return;
                                        }

                                        await submitQuotationPayload(payload);
                                    }}
                                    disabled={loading}
                                    className={`px-4 py-2 text-sm font-medium text-white rounded-sm ${loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                                        }`}
                                >
                                    {loading ? "Submitting..." : "Confirm Submission"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div >
    )
}

export default LabourQuotationForm

